import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import api from "../../api";

export default function PresenceListPage() {
  const { t } = useTranslation();
  const [presences, setPresences] = useState([]);
  const [statsMap, setStatsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  const handleOpenPresence = (presence) => {
    // Deleted presence - do nothing (card should be disabled)
    if (presence.status === "deleted") return;

    // No consent & owner - redirect to consent page
    if (!presence.hasConsent && presence.isOwner) {
      navigate(`/presence/${presence.id}/consent`);
      return;
    }

    // No chat permission (viewer) - redirect to activity
    if (!presence.effectivePermissions?.includes("chat")) {
      navigate(`/presence/${presence.id}/activity`);
      return;
    }

    // Paused presence - redirect to activity
    if (presence.status === "paused") {
      navigate(`/presence/${presence.id}/activity`);
      return;
    }

    // Default: active + consent + chat permission → Chat
    navigate(`/presence/${presence.id}/chat`);
  };

  const handlePause = async (presence) => {
    // TODO: /api/presence/{id}/pause endpoint çağır
    console.log("Pause presence", presence.id);
  };

  const handleResume = async (presence) => {
    // TODO: /api/presence/{id}/resume
    console.log("Resume presence", presence.id);
  };

  const openShareDialog = (presence) => {
    // TODO: share modal aç
    console.log("Share presence", presence.id);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await api.get("/presence/list");
        const list = res.data.presences || [];
        setPresences(list);

        // Stats'leri paralel çek
        const statCalls = list.map((p) =>
          api
            .get(`/logs/statistics/${p.id}?days=30`)
            .then((r) => ({ id: p.id, stats: r.data.statistics || r.data }))
            .catch(() => ({ id: p.id, stats: null }))
        );
        const results = await Promise.all(statCalls);
        const map = {};
        results.forEach((r) => {
          map[r.id] = r.stats;
        });
        setStatsMap(map);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredPresences = presences.filter((p) => {
    if (filter === "all") return true;
    if (filter === "active") return p.status === "active";
    if (filter === "paused") return p.status === "paused";
    return true;
  });

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 flex flex-col">
      {/* Top bar */}
      <header className="border-b border-slate-900 px-6 py-4 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-400/80 mb-1">
            RepresenX
          </div>
          <div className="text-xl font-semibold flex items-center gap-2">
            {t('presenceList.title')}
            <span className="text-xs text-slate-500 font-normal">
              ({presences.length} {t('presenceList.totalCount')})
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <FilterChip
            label={t('presenceList.filterAll')}
            active={filter === "all"}
            onClick={() => setFilter("all")}
          />
          <FilterChip
            label={t('presenceList.filterActive')}
            active={filter === "active"}
            onClick={() => setFilter("active")}
          />
          <FilterChip
            label={t('presenceList.filterPaused')}
            active={filter === "paused"}
            onClick={() => setFilter("paused")}
          />
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 px-6 py-6">
        {loading ? (
          <PresenceSkeletonGrid />
        ) : filteredPresences.length === 0 ? (
          <EmptyState />
        ) : (
          <motion.div
            layout
            className="grid gap-4 md:grid-cols-2 xl:grid-cols-3"
          >
            <AnimatePresence>
              {filteredPresences.map((p, index) => (
                <PresenceCard
                  key={p.id}
                  presence={p}
                  stats={statsMap[p.id]}
                  index={index}
                  onOpen={() => handleOpenPresence(p)}
                  onPause={() => handlePause(p)}
                  onResume={() => handleResume(p)}
                  onSettings={() => navigate(`/presence/${p.id}/settings`)}
                  onShare={() => openShareDialog(p)}
                />
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </main>
    </div>
  );
}

function FilterChip({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={[
        "px-3 py-1 rounded-full border text-[11px] tracking-wide transition",
        active
          ? "border-cyan-400 bg-cyan-400/10 text-cyan-200 shadow-[0_0_12px_rgba(34,211,238,0.6)]"
          : "border-slate-700 text-slate-300 hover:border-cyan-500/60 hover:text-cyan-100",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

function PresenceCard({ presence, stats, index, onOpen, onPause, onResume, onSettings, onShare }) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  
  const statusColor =
    presence.status === "active"
      ? "bg-emerald-400/80"
      : presence.status === "paused"
      ? "bg-amber-400/80"
      : "bg-slate-500";

  const statusLabel =
    presence.status === "active"
      ? t('presenceList.statusActive')
      : presence.status === "paused"
      ? t('presenceList.statusPaused')
      : t('presenceList.statusDeleted');

  const roleLabel = presence.isOwner ? t('presenceList.owner') : t('presenceList.shared');
  const roleColor = presence.isOwner
    ? "bg-sky-500/15 border-sky-400/60 text-sky-200"
    : "bg-purple-500/12 border-purple-400/60 text-purple-200";

  const lastActivity = presence.lastActivityAt
    ? new Date(presence.lastActivityAt)
    : null;

  // Online badge ping
  const online = presence.online && presence.status === "active";

  const toggleMenu = (e) => {
    e.stopPropagation();
    setMenuOpen((o) => !o);
  };

  const handleAction = (type) => {
    setMenuOpen(false);
    if (type === "pause" && onPause) onPause();
    if (type === "resume" && onResume) onResume();
    if (type === "settings" && onSettings) onSettings();
    if (type === "share" && onShare) onShare();
  };

  const isDeleted = presence.status === "deleted";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.96 }}
      transition={{
        type: "spring",
        stiffness: 260,
        damping: 22,
        mass: 0.6,
        delay: index * 0.03,
      }}
      whileHover={isDeleted ? {} : {
        y: -4,
        scale: 1.02,
        boxShadow: "0 0 24px rgba(34,211,238,0.45)",
      }}
      className={[
        "relative overflow-hidden rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-950 via-slate-950/95 to-slate-950/80 group",
        isDeleted ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
      ].join(" ")}
      onClick={isDeleted ? undefined : onOpen}
    >
      {/* Glow overlay */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-cyan-500/10 via-transparent to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />

      <div className="relative p-4 flex flex-col gap-3">
        {/* Header line */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="h-9 w-9 rounded-2xl bg-slate-900 border border-slate-700 flex items-center justify-center text-xs font-semibold text-cyan-200">
                {presence.name?.[0]?.toUpperCase() || "P"}
              </div>

              {/* Status dot */}
              <span
                className={`absolute -bottom-1 -right-1 h-2 w-2 rounded-full border border-slate-900 ${statusColor}`}
              />

              {/* Online ping overlay */}
              {online && (
                <span className="absolute -top-1 -left-1 inline-flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-400" />
                </span>
              )}
            </div>

            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-50 line-clamp-1">
                {presence.name || t('presenceList.unnamed')}
              </div>
              <div className="flex items-center gap-2 text-[11px] text-slate-500">
                <span>
                  {t('presenceList.created')} {new Date(presence.createdAt).toLocaleDateString()}
                </span>
                {lastActivity && (
                  <>
                    <span className="h-1 w-1 rounded-full bg-slate-600" />
                    <span>
                      {t('presenceList.lastChat')}{" "}
                      {lastActivity.toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                      })}{" "}
                      ·{" "}
                      {lastActivity.toLocaleTimeString(undefined, {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Status + role badges + quick actions */}
          <div className="flex flex-col items-end gap-1 relative">
            <span className="px-2 py-0.5 rounded-full bg-slate-900 border border-slate-700 text-[10px] uppercase tracking-[0.14em] text-slate-300">
              {statusLabel}
            </span>
            <span
              className={[
                "px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-[0.14em]",
                roleColor,
              ].join(" ")}
            >
              {roleLabel}
            </span>

            {/* Quick actions trigger */}
            <button
              className="mt-1 inline-flex items-center justify-center h-6 w-6 rounded-full bg-slate-900/90 border border-slate-700 text-slate-400 text-xs hover:border-cyan-400/80 hover:text-cyan-100 transition"
              onClick={toggleMenu}
            >
              ⋯
            </button>

            {/* Dropdown menu */}
            <QuickActionsMenu
              open={menuOpen}
              isOwner={!!presence.isOwner}
              status={presence.status}
              onAction={handleAction}
            />
          </div>
        </div>

        {/* Description */}
        <div className="text-xs text-slate-300 line-clamp-2 min-h-[2.5rem]">
          {presence.description || t('presenceList.noDescription')}
        </div>

        {/* Stats line */}
        <div className="flex items-center justify-between gap-2 text-[11px] mt-1">
          <div className="flex flex-col">
            <span className="text-slate-500">{t('presenceList.chats30d')}</span>
            <span className="text-cyan-300 font-semibold">
              {stats?.totalLLMInteractions ?? "—"}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-slate-500">{t('presenceList.blocked')}</span>
            <span className="text-amber-300 font-semibold">
              {stats?.totalBlocked ?? "—"}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-slate-500">{t('presenceList.withMemory')}</span>
            <span className="text-emerald-300 font-semibold">
              {stats?.totalMemoryInjections ?? "—"}
            </span>
          </div>
        </div>

        {/* CTA line */}
        <div className="mt-2 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[11px] text-slate-500">
            {online ? (
              <>
                <span className="inline-flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-emerald-400 opacity-80" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                </span>
                <span>{t('presenceList.online')}</span>
              </>
            ) : (
              <span>{t('presenceList.idle')}</span>
            )}
          </div>
          <motion.button
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
            whileTap={{ scale: 0.9 }}
            className="px-3 py-1 text-[11px] rounded-full bg-cyan-400 text-slate-900 font-semibold shadow-[0_0_14px_rgba(34,211,238,0.7)] group-hover:bg-cyan-300 transition"
          >
            {t('presenceList.openWorkspace')}
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
}

function PresenceSkeletonGrid() {
  const placeholders = Array.from({ length: 6 });
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {placeholders.map((_, idx) => (
        <SkeletonCard key={idx} />
      ))}
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-950/90 p-4 animate-pulse space-y-3">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-2xl bg-slate-800" />
        <div className="flex-1 space-y-1">
          <div className="h-3 w-32 bg-slate-800 rounded" />
          <div className="h-2 w-20 bg-slate-900 rounded" />
        </div>
      </div>
      <div className="h-3 w-full bg-slate-900 rounded" />
      <div className="h-3 w-4/5 bg-slate-900 rounded" />
      <div className="flex gap-3 mt-2">
        <div className="h-4 w-12 bg-slate-900 rounded" />
        <div className="h-4 w-12 bg-slate-900 rounded" />
        <div className="h-4 w-16 bg-slate-900 rounded" />
      </div>
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div className="h-[60vh] flex flex-col items-center justify-center text-center">
      <div className="mb-3 text-3xl">🜲</div>
      <div className="text-sm text-slate-200 mb-1">
        {t('presenceList.emptyTitle')}
      </div>
      <div className="text-xs text-slate-500">
        {t('presenceList.emptyDesc')}
      </div>
    </div>
  );
}

function QuickActionsMenu({ open, isOwner, status, onAction }) {
  const { t } = useTranslation();
  
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -4, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -4, scale: 0.96 }}
          transition={{ type: "spring", stiffness: 260, damping: 20, mass: 0.7 }}
          className="absolute top-11 right-0 z-20 w-44 rounded-xl bg-slate-950 border border-slate-800 shadow-xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="py-1 text-[11px] text-slate-200">
            {/* Resume / Pause – sadece owner ve status uygun ise */}
            {isOwner && status === "paused" && (
              <MenuItem onClick={() => onAction("resume")}>
                <span className="text-emerald-300 mr-1.5">▶</span>
                {t('presenceList.resumePresence')}
              </MenuItem>
            )}
            {isOwner && status === "active" && (
              <MenuItem onClick={() => onAction("pause")}>
                <span className="text-amber-300 mr-1.5">⏸</span>
                {t('presenceList.pausePresence')}
              </MenuItem>
            )}

            {/* Share */}
            {isOwner && (
              <MenuItem onClick={() => onAction("share")}>
                <span className="text-sky-300 mr-1.5">✦</span>
                {t('presenceList.shareAccess')}
              </MenuItem>
            )}

            {/* Settings – owner veya contributor'a açık olabilir */}
            <MenuItem onClick={() => onAction("settings")}>
              <span className="text-cyan-300 mr-1.5">⚙</span>
              {t('presenceList.settings')}
            </MenuItem>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function MenuItem({ children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center px-3 py-2 text-left hover:bg-slate-900/80 hover:text-cyan-100 transition"
    >
      {children}
    </button>
  );
}
